import React, { memo, useState, UIEvent } from "react";
import { 
    Box, 
    Typography, 
    List, 
    ListItemButton, 
    ListItemText, 
    Tooltip, 
    CircularProgress, 
    IconButton,
    Paper,
    Button,
    useMediaQuery
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { PreviousChat } from "../../../../../shared/types/chatWindow.types";

interface ChatHistorySidebarProps {
    isOpen: boolean;
    previousChats?: PreviousChat[];
    isFetchingMore?: boolean;
    onClose: () => void;
    onSelectChat?: (chatId: string) => void;
    onLoadMore?: () => void;
    onDeleteChat?: (chatId: string) => void;
}

const ChatHistorySidebar: React.FC<ChatHistorySidebarProps> = memo(({
    isOpen,
    previousChats,
    isFetchingMore,
    onClose,
    onSelectChat,
    onLoadMore,
    onDeleteChat,
}) => {
    const [hoveredChatId, setHoveredChatId] = useState<string | null>(null);
    const [chatToDelete, setChatToDelete] = useState<{ id: string; title: string } | null>(null);
    
    const isTouch = useMediaQuery("(hover: none)");

    const handleScroll = (e: UIEvent<HTMLUListElement>) => {
        const target = e.currentTarget;
        if (target.scrollHeight - target.scrollTop <= target.clientHeight + 20) {
            onLoadMore?.();
        }
    };

    const handleChatClick = (chatId: string) => {
        setHoveredChatId(null); 
        onSelectChat?.(chatId);
        onClose();
    };

    const handleConfirmDelete = () => {
        if (chatToDelete) {
            onDeleteChat?.(chatToDelete.id);
            setChatToDelete(null);
        }
    };

    return (
        <>
            {isOpen && (
                <Box
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 90,
                        bgcolor: "rgba(0,0,0,0.1)",
                    }}
                />
            )}

            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    bottom: 0,
                    width: "260px",
                    bgcolor: "#e4edef",
                    borderRight: "1px solid #c9d7dd",
                    zIndex: 100,
                    display: "flex",
                    flexDirection: "column",
                    transform: isOpen ? "translateX(0)" : "translateX(-100%)",
                    transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxShadow: isOpen ? "4px 0 15px rgba(0,0,0,0.05)" : "none",
                }}
            >
                <Box sx={{ p: 2, pb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#073857">
                        Previous messages
                    </Typography>
                </Box>

                <List sx={{ flex: 1, overflowY: "auto", p: 0 }} onScroll={handleScroll}>
                    {previousChats?.map((chat) => (
                        <ListItemButton
                            key={chat.id}
                            onMouseEnter={() => isOpen && setHoveredChatId(chat.id)}
                            onMouseLeave={() => setHoveredChatId(null)}
                            onClick={() => handleChatClick(chat.id)}
                            sx={{
                                borderBottom: "2px solid rgba(0,0,0,0.04)",
                                py: 1.5,
                                px: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                "&:hover": { bgcolor: "rgba(255,255,255,0.4)" },
                                "&:hover .delete-btn": { opacity: 1, pointerEvents: "auto" },
                                "@media (hover: none)": {
                                    "& .delete-btn": { opacity: 1, pointerEvents: "auto" }
                                }
                            }}
                        >
                            <Tooltip
                                title={chat.title}
                                placement="bottom-start"
                                arrow
                                open={isTouch ? undefined : (isOpen && hoveredChatId === chat.id)}
                                disableInteractive
                                enterDelay={300}
                                leaveDelay={0}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}> 
                                    <ListItemText
                                        primary={chat.title}
                                        primaryTypographyProps={{
                                            fontSize: "0.85rem",
                                            fontWeight: 600,
                                            color: "#1e293b",
                                            noWrap: true,
                                        }}
                                        sx={{ m: 0 }}
                                    />
                                </Box>
                            </Tooltip>
                            
                            <IconButton
                                className="delete-btn"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation(); 
                                    setHoveredChatId(null); 
                                    setChatToDelete({ id: chat.id, title: chat.title }); 
                                }}
                                sx={{
                                    opacity: 0,
                                    pointerEvents: "none", 
                                    flexShrink: 0,         
                                    width: 28,
                                    height: 28,
                                    transition: "all 0.2s",
                                    color: "#94a3b8",
                                    "&:hover": { color: "#ef4444", bgcolor: "rgba(239,68,68,0.1)" },
                                }}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </ListItemButton>
                    ))}

                    {isFetchingMore && (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                            <CircularProgress size={20} sx={{ color: "#5FB7CF" }} />
                        </Box>
                    )}

                    {!isFetchingMore && !previousChats?.length && (
                        <Box sx={{ p: 2, color: "#64748b", fontSize: "0.85rem", textAlign: "center" }}>
                            No previous messages found.
                        </Box>
                    )}
                </List>
            </Box>

            {/* Delete Modal */}
            {chatToDelete && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 2000,
                        borderRadius: "12px",
                        bgcolor: "rgba(0,0,0,0.5)",
                        backdropFilter: "blur(2px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 2,
                    }}
                    onClick={() => setChatToDelete(null)}
                >
                    <Paper
                        elevation={6}
                        onClick={(e) => e.stopPropagation()} 
                        sx={{
                            p: 3,
                            borderRadius: "12px",
                            textAlign: "center",
                            width: "100%",
                            maxWidth: 320,
                            bgcolor: "white",
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight={700} color="#0f172a" mb={1}>
                            Delete Conversation
                        </Typography>
                        <Typography variant="body2" color="#64748b" mb={3}>
                            Are you sure you want to delete "<strong>{chatToDelete.title}</strong>"?
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center" }}>
                            <Button
                                variant="outlined"
                                onClick={() => setChatToDelete(null)}
                                sx={{
                                    flex: 1,
                                    borderColor: "#cbd5e1",
                                    color: "#475569",
                                    textTransform: "none",
                                    "&:hover": { bgcolor: "#f8fafc", borderColor: "#94a3b8" }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleConfirmDelete}
                                sx={{
                                    flex: 1,
                                    bgcolor: "#ef4444",
                                    color: "white",
                                    textTransform: "none",
                                    boxShadow: "none",
                                    "&:hover": { bgcolor: "#dc2626", boxShadow: "none" },
                                }}
                            >
                                Delete
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            )}
        </>
    );
});

export default ChatHistorySidebar;